import React, { useEffect, useMemo, useState } from 'react';
import { useTable, useGlobalFilter, useSortBy } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import { faEdit, faTrash, faSort } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { CampaignService, CampaignTypeService } from '../../../../Services/Axios/Api';
import { toast } from 'react-toastify';
import CampaignOrderEditor from '../../../../Components/CampaignOrderEditor/CampaignOrderEditor';

const Campaigns = () => {
    const [campaignData, setCampaignData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [campaignTypes, setCampaignTypes] = useState([]);
    const [showOrderEditor, setShowOrderEditor] = useState(false);
    const navigate = useNavigate();

    const fetchCampaigns = async () => {
        try {
            setLoading(true);
            const response = await CampaignService.getCampaigns();
            setCampaignData(response.result);
        } catch (error) {
            toast.error('Failed to fetch campaigns');
            console.error('Error fetching campaigns:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCampaignTypes = async () => {
        try {
            const response = await CampaignTypeService.getCampaignTypes();
            setCampaignTypes(response.result);
        } catch (error) {
            toast.error('Failed to fetch campaign types');
            console.error('Error fetching campaign types:', error);
        }
    };

    useEffect(() => {
        fetchCampaigns();
        fetchCampaignTypes();
    }, []);

    const getCampaignTypeName = (typeId) => {
        const type = campaignTypes.find(t => t.id === typeId);
        return type ? type.name : 'Unknown Type';
    };

    const columns = useMemo(
        () => [
            {
                Header: 'ID',
                accessor: 'id',
                Cell: ({ value }) => (
                    <span className="text-muted">#{value}</span>
                ),
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Type',
                accessor: 'campaignSectionId',
                Cell: ({ value }) => getCampaignTypeName(value),
            },
            {
                Header: 'Description',
                accessor: 'description',
                Cell: ({ value }) => (
                    <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {value}
                    </div>
                ),
            },
            {
                Header: 'Target',
                accessor: 'targetAmount',
                Cell: ({ value, row }) => {
                    const type = campaignTypes.find(t => t.id === row.original.campaignSectionId);
                    return type?.mode === 'Progressing' ? `$${value || 0}` : 'N/A';
                },
            },
            {
                Header: 'Recurring',
                accessor: 'isRecurring',
                Cell: ({ value, row }) => (
                    <div>
                        <span className={`badge ${value ? 'bg-info' : 'bg-secondary'}`}>
                            {value ? 'Recurring' : 'Normal'}
                        </span>
                        {value && row.original.recurringMode && (
                            <span className="badge bg-light text-dark ms-1">
                                {row.original.recurringMode === 'nextMonth' ? 'Next Month' : 'Calendar Month'}
                            </span>
                        )}
                    </div>
                ),
            },
            {
                Header: 'Status',
                accessor: 'isActive',
                Cell: ({ value }) => (
                    <span className={`badge ${value ? 'bg-success' : 'bg-danger'}`}>
                        {value ? 'Active' : 'Inactive'}
                    </span>
                ),
            },
            {
                Header: 'Visible',
                accessor: 'isVisible',
                Cell: ({ value }) => (
                    <span className={`badge ${value !== false ? 'bg-success' : 'bg-warning'}`}>
                        {value !== false ? 'Visible' : 'Hidden'}
                    </span>
                ),
            },
            {
                Header: 'Actions',
                Cell: ({ row }) => (
                    <div className="action-buttons">
                        <button 
                            className="btn btn-outline-primary btn-sm me-2 m-1" 
                            onClick={() => navigate(`edit/${row.original.id}`)}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button 
                            className="btn btn-outline-danger btn-sm m-1" 
                            onClick={() => openDeleteModal(row.original)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                ),
            },
        ],
        [campaignTypes]
    );

    const openDeleteModal = (campaign) => {
        setSelectedCampaign(campaign);
        setShowModal(true);
    };

    const handleDelete = async () => {
        if (!selectedCampaign) return;

        try {
            setLoading(true);
            await CampaignService.deleteCampaign(selectedCampaign.id);
            toast.success('Campaign deleted successfully');
            await fetchCampaigns();
        } catch (error) {
            toast.error('Failed to delete campaign');
            console.error('Error deleting campaign:', error);
        } finally {
            setLoading(false);
            setShowModal(false);
            setSelectedCampaign(null);
        }
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
        setGlobalFilter,
    } = useTable(
        { 
            columns, 
            data: campaignData,
            initialState: {
                sortBy: [{ id: 'name', desc: false }]
            }
        },
        useGlobalFilter,
        useSortBy
    );

    const { globalFilter } = state;

    if (loading && campaignData.length === 0) {
        return (
            <div className="container mt-5">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Campaigns</h2>
                <div className="d-flex gap-2">
                    <button 
                        className="btn btn-outline-primary" 
                        onClick={() => setShowOrderEditor(true)}
                    >
                        <FontAwesomeIcon icon={faSort} className="me-2" />
                        Edit Order
                    </button>
                    <button className="btn btn-success" onClick={() => navigate('add')}>
                        Add New Campaign
                    </button>
                </div>
            </div>

            <div className="d-flex justify-content-end align-items-center mb-3">
                <div style={{ maxWidth: '320px', width: '100%' }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search campaigns..."
                        value={globalFilter || ''}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover" {...getTableProps()}>
                    <thead className="thead-light">
                        {headerGroups.map(headerGroup => {
                            const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                            return (
                                <tr key={key} {...restHeaderGroupProps}>
                                    {headerGroup.headers.map(column => {
                                        const { key: columnKey, ...restHeaderProps } = column.getHeaderProps(column.getSortByToggleProps());
                                        return (
                                            <th key={columnKey} {...restHeaderProps}>
                                                {column.render('Header')}
                                                <span className="ms-1">
                                                    {column.isSorted
                                                        ? column.isSortedDesc
                                                            ? ' 🔽'
                                                            : ' 🔼'
                                                        : ''}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the campaign "{selectedCampaign?.name}"?
                    This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDelete}
                        disabled={loading}
                    >
                        {loading ? (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <CampaignOrderEditor
                show={showOrderEditor}
                onHide={() => setShowOrderEditor(false)}
                campaigns={campaignData}
                onSave={fetchCampaigns}
            />
        </div>
    );
};

export default Campaigns;
